(() => {
  const BTN_SELECTOR = "[data-swatch-add]";

  // ---------------------------
  // Button UI helpers
  // ---------------------------
  const setText = (btn, text) => {
    if (!btn) return;
    btn.textContent = text;
  };

  const setState = (btn, state) => {
    // state: 'idle' | 'adding' | 'added'
    const idle = btn.getAttribute("data-text-idle") || "Add swatch to cart";
    const adding = btn.getAttribute("data-text-adding") || "Adding…";
    const added = btn.getAttribute("data-text-added") || "Swatch in Cart";

    btn.dataset.state = state;

    if (state === "adding") {
      btn.disabled = true;
      setText(btn, adding);
      return;
    }

    if (state === "added") {
      btn.disabled = false;
      setText(btn, added);
      return;
    }

    btn.disabled = false;
    setText(btn, idle);
  };

  // ---------------------------
  // Cart + Drawer helpers
  // ---------------------------
  const getCartDrawer = () => document.querySelector("cart-drawer");

  const fetchCart = async () => {
    const res = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Failed to fetch cart.js");
    return res.json();
  };

  const cartHasVariant = (cart, variantId) => {
    const id = Number(variantId);
    return (cart.items || []).some((i) => Number(i.id) === id);
  };

  // Trigger the SAME event Broadcast uses after successful add-to-cart.
  const dispatchBroadcastAddEvent = (buttonEl, responseJson) => {
    try {
      buttonEl.dispatchEvent(
        new CustomEvent("theme:product:add", {
          detail: { response: responseJson, button: buttonEl },
          bubbles: true,
        }),
      );
    } catch (e) {}
  };

  // Try Broadcast native drawer refresh.
  //
  // The cart-count badge (in header + drawer) only updates when the
  // 'theme:cart:change' event fires on document with detail.cartCount —
  // that event is dispatched by CartItems.build(), which runs at the end
  // of CartItems.getCart(). The <cart-drawer> custom element does NOT
  // define getCart(); the <cart-items> element nested inside it does. So
  // reach through the drawer to call getCart() on the inner cart-items
  // element. Fall back to the first cart-items on the page (e.g. cart
  // page) if no drawer is present.
  const tryDrawerGetCart = () => {
    const drawer = getCartDrawer();
    const cartItems =
      (drawer && drawer.querySelector("cart-items")) ||
      document.querySelector("cart-items");
    if (cartItems && typeof cartItems.getCart === "function") {
      try {
        cartItems.getCart();
        return true;
      } catch (e) {}
    }
    return false;
  };

  // Absolute fallback: refresh cart-drawer section HTML via sections API
  // (safe — this is Shopify/Broadcast’s own markup, not custom DOM construction)
  const refreshDrawerSectionHTML = async () => {
    const sectionEl = document.querySelector(
      '[data-section-type="cart-drawer"]',
    );
    if (!sectionEl) return false;

    const sectionId = sectionEl.getAttribute("data-section-id");
    if (!sectionId) return false;

    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;
    const res = await fetch(url, {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!res.ok) return false;

    const json = await res.json();
    const html = json[sectionId];
    if (!html) return false;

    // Replace the section outer HTML
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;

    const newSection = wrapper.querySelector(
      `[data-section-id="${CSS.escape(sectionId)}"]`,
    );
    if (!newSection) return false;

    sectionEl.replaceWith(newSection);
    return true;
  };

  // Sync all swatch buttons on the page from actual cart contents
  const syncButtonsFromCart = (cart) => {
    document.querySelectorAll(BTN_SELECTOR).forEach((btn) => {
      const variantId = Number(btn.getAttribute("data-variant-id"));
      if (!Number.isFinite(variantId)) return;
      setState(btn, cartHasVariant(cart, variantId) ? "added" : "idle");
    });
  };

  // ---------------------------
  // Add swatch handler
  // ---------------------------
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest(BTN_SELECTOR);
    if (!btn) return;

    e.preventDefault();

    // If already added, do nothing (per your preference)
    if (btn.dataset.state === "added") return;

    const variantId = Number(btn.getAttribute("data-variant-id"));
    if (!Number.isFinite(variantId)) return;

    setState(btn, "adding");

    try {
      // Add to cart (use Broadcast preferred route if available)
      const addUrl =
        (window.theme && theme.routes && theme.routes.cart_add_url) ||
        "/cart/add.js";

      const formData = new FormData();
      formData.append("id", String(variantId));
      formData.append("quantity", "1");

      const res = await fetch(addUrl, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/javascript",
        },
        body: formData,
        credentials: "same-origin",
      });

      const addResponse = await res.json();

      // Broadcast uses "status" in JSON to indicate an error
      if (addResponse && addResponse.status) {
        setState(btn, "idle");
        return;
      }

      // ✅ Confirm it truly exists in cart BEFORE showing “Added ✓”
      const cartAfterAdd = await fetchCart();
      if (!cartHasVariant(cartAfterAdd, variantId)) {
        // Added call succeeded but cart doesn't contain it (rare, but we've seen this)
        setState(btn, "idle");
        return;
      }

      // Now we can safely show Added ✓
      setState(btn, "added");

      // Trigger Broadcast add event (this is the most “native” refresh path)
      dispatchBroadcastAddEvent(btn, addResponse);

      // Backup: call drawer.getCart()
      tryDrawerGetCart();

      // If drawer still visually stale after a beat, replace section HTML (last resort)
      setTimeout(async () => {
        try {
          // If the count UI still looks stale, force refresh section HTML
          // (We can’t reliably read the count state, so we just do a safe refresh.)
          await refreshDrawerSectionHTML();
        } catch (e) {}
      }, 250);

      // Keep buttons in sync globally too
      syncButtonsFromCart(cartAfterAdd);
    } catch (err) {
      console.error(err);
      setState(btn, "idle");
    }
  });

  // Initial sync on load
  window.addEventListener("DOMContentLoaded", async () => {
    try {
      const cart = await fetchCart();
      syncButtonsFromCart(cart);
    } catch (e) {}
  });

  window.addEventListener("DOMContentLoaded", async () => {
    try {
      const cart = await fetchCart();
      syncButtonsFromCart(cart);
    } catch (e) {}
  });

  // Re-sync swatch buttons after cart drawer updates (remove / qty changes)
  const resyncSoon = () => {
    // small delay so Broadcast can finish its cart update first
    setTimeout(async () => {
      try {
        const cart = await fetchCart();
        syncButtonsFromCart(cart);
      } catch (e) {}
    }, 150);
  };

  // Broadcast theme events (best case)
  document.addEventListener("theme:cart:updated", resyncSoon);
  document.addEventListener("theme:cart:change", resyncSoon);

  // Generic Shopify / theme patterns
  document.addEventListener("cart:updated", resyncSoon);
  document.addEventListener("cart:refresh", resyncSoon);

  // Fallback: watch the cart drawer DOM for changes (covers remove links)
  const watchDrawer = () => {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer) return;

    const target =
      drawer.querySelector("[data-items-holder]") ||
      drawer.querySelector(".drawer__items") ||
      drawer;

    if (!target) return;

    const observer = new MutationObserver(() => resyncSoon());
    observer.observe(target, { childList: true, subtree: true });
  };

  watchDrawer();
})();
